// src/pages/FmFinalizePopup.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ImCross } from "react-icons/im";
import PreloadFetch from "../components/PreloadFetch";
import { handlePriceInput } from "../utils/inputUtils";

const PF_OPTIONS = [
    { value: "1", label: "Semanal" },
    { value: "2", label: "Quincenal" },
    { value: "3", label: "Mensual" },
];

const ordinalsTime = [
    { singular: "Semana", plural: "Semanas" },
    { singular: "Quincena", plural: "Quincenas" },
    { singular: "Mes", plural: "Meses" },
];

const fmt2 = (n) =>
    new Intl.NumberFormat("es-PE", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(n || 0));

const getAuthHeaders = () => {
    const possibles = ["token", "accessToken", "access_token", "authToken", "jwt"];
    let token = localStorage.getItem("token");
    if (!token) {
        for (const k of possibles) {
            const v = localStorage.getItem(k);
            if (v) { token = v; break; }
        }
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
    baseURL: process.env.REACT_APP_API,
    withCredentials: true,
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
});

export default function FmFinalizePopup({
    open,
    onClose,
    initial,     // { segment, amountFM, dni, name, paternalSur, maternalSur, email, addressName, phoneNumber, modelPhone, imei }
    onCreated,
    // Si true, se comporta como Payjoy (pf semanal), pero guarda el segment real pasado en initial.segment
    forcePayjoyMode = false,
    hideCuota
}) {
    // console.log(hideCuota);
    // console.log(initial);
    const [loading, setLoading] = useState(false);

    // segment real (se guarda)
    const segment = String(initial?.segment ?? "");
    const category = String(initial?.cat ?? initial?.category ?? "");
    const subCategory = String(initial?.subCat ?? initial?.subCategory ?? "");

    // IMPORTANTE:
    // segment=4 se usa en dos contextos distintos:
    // - CRÉDITO(1) / CELULAR(1) / CREDISMART(4)
    // - CONTADO(2) / CELULAR(1) / MOVILPRO(4)
    // Por eso nunca se debe decidir solo con segment.
    const isCrediSmartCredit = category === "1" && subCategory === "1" && segment === "4";
    const isAloCredit = segment === "5";

    // segment efectivo para UI/lógica heredada.
    // CrediSmart NO se fuerza a Payjoy aquí porque necesita Semanal, Quincenal y Mensual.
    const effectiveSegment = forcePayjoyMode && !isCrediSmartCredit ? "1" : segment;

    const segmentName = useMemo(() => {
        if (isCrediSmartCredit) return "CrediSmart";
        if (isAloCredit) return "Alo Credit";
        if (effectiveSegment === "1") return "Payjoy";
        if (effectiveSegment === "2") return "MovilesPay";
        if (effectiveSegment === "3") return "Efectiva";
        if (effectiveSegment === "4") return "MovilPro";
        return "Segmento";
    }, [effectiveSegment, isCrediSmartCredit, isAloCredit]);

    const [pf, setPf] = useState("1");
    const [numFee, setNumFee] = useState("");
    const [amountSource, setAmountSource] = useState("");
    const [addressName, setAddressName] = useState(initial?.addressName || "");
    const amountFM = Number(initial?.amountFM || 0);

    const fee = useMemo(() => {
        const n = parseInt(numFee || "1", 10);
        if (!n || !amountFM) return "0.00";
        return (amountFM / n).toFixed(2);
    }, [numFee, amountFM]);

    // Al abrir: domicilio y PF por segmento efectivo
    useEffect(() => {
        if (!open) return;
        setAddressName(initial?.addressName || "");
        if (isCrediSmartCredit || isAloCredit) setPf("1"); // CrediSmart/Alo Credit inician semanal y permiten cambiar
        else if (effectiveSegment === "1") setPf("1"); // Payjoy semanal
        else if (effectiveSegment === "3") setPf("3"); // Efectiva mensual
        else if (effectiveSegment === "4") setPf("1"); // MovilPro/contado: fallback seguro
    }, [open, initial?.addressName, effectiveSegment, isCrediSmartCredit, isAloCredit]);

    // MovilesPay: traer cuota desde crédito (solo si effectiveSegment === "2")
    useEffect(() => {
        let cancel = false;
        async function loadCreditConfig() {
            try {
                console.log(effectiveSegment);
                if (!open || effectiveSegment !== "2") return;
                const dni = String(initial?.dni || "");
                if (dni.length !== 8) return;

                const { data } = await api.post("/credit/payment-frequency", { dni });
                // console.log('DATA FFFF');
                // console.log(data);
                if (!cancel && data?.msg === "success") {
                    if (data?.pf) setPf(String(data.pf));
                    const apiFee = data?.fee ?? data?.fr_fee;
                    if (apiFee !== undefined && apiFee !== null && apiFee !== "") {
                        setAmountSource(fmt2(apiFee));
                    }
                }
            } catch {}
        }
        loadCreditConfig();
        return () => { cancel = true; };
    }, [open, effectiveSegment, initial?.dni]);

    // Opciones PF visibles según segmento efectivo
    const pfOptions = useMemo(() => {
        if (isCrediSmartCredit || isAloCredit) return PF_OPTIONS; // CrediSmart/Alo Credit: Semanal, Quincenal y Mensual
        if (effectiveSegment === "1") return PF_OPTIONS.filter(o => o.value === "1"); // Payjoy semanal
        if (effectiveSegment === "3") return PF_OPTIONS.filter(o => o.value === "3"); // Efectiva mensual
        if (effectiveSegment === "2") return PF_OPTIONS.filter(o => o.value === String(pf)); // MovilesPay fija
        if (effectiveSegment === "4") return PF_OPTIONS.filter(o => o.value === "1"); // MovilPro/contado: fallback seguro
        return PF_OPTIONS;
    }, [effectiveSegment, pf, isCrediSmartCredit, isAloCredit]);

    const pfDisabled = !(isCrediSmartCredit || isAloCredit);
    const amountSourceReadOnly = effectiveSegment === "2";

    const onAmountSourceInput = (e) => {
        handlePriceInput(e);
        setAmountSource(e.target.value);
    };

    const close = () => { if (!loading) onClose?.(); };

    const parseMoney = (s) => {
        if (!s) return 0;
        const unified = String(s).trim().replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
        const n = Number(unified);
        return isNaN(n) ? 0 : n;
    };

    const resetForm = () => {
        setAddressName("");
        setPf("");
        setNumFee("");
        // setAmountFM("");
        // setAmountCuota("");
        setAmountSource("");
        // setSchedule([]);
    };

    const validate = () => {
        if (!amountFM || amountFM <= 0) { alert("Monto FM inválido."); return false; }
        const n = parseInt(numFee, 10);
        if (!n || isNaN(n) || n < 1 || n > 10) { alert("Selecciona un número de cuotas entre 1 y 10."); return false; }
        if (!addressName || addressName.trim().length < 3) { alert("Ingresa un domicilio válido."); return false; }
        if (effectiveSegment === "2") {
            const dni = String(initial?.dni || "");
            if (dni.length !== 8) { alert("DNI inválido para recuperar la frecuencia de pago."); return false; }
            const asNum = parseMoney(amountSource);
            if (!asNum || asNum <= 0) { alert("La cuota del crédito no se pudo obtener. Verifica el DNI."); return false; }
        }
        return true;
    };

    const save = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            const form = new FormData();
            // Guardamos el segment REAL
            form.append("source", segment);
            form.append("amountSource", Number(amountSource || 0).toFixed(2));
            form.append("fullName", initial?.name || "");
            form.append("lastName", `${initial?.paternalSur || ""} ${initial?.maternalSur || ""}`.trim());
            form.append("dni", initial?.dni || "");
            form.append("email", initial?.email || "");
            form.append("addressName", addressName || "");
            form.append("phoneNumber", initial?.phoneNumber || "");
            form.append("amountFM", String(amountFM.toFixed(2)));
            form.append("numFee", String(numFee));
            form.append("countFeePay", "0");
            form.append("modelPhone", initial?.modelPhone || "");
            form.append("paymentFrequency", String(pf)); // semanal/quincenal/mensual
            form.append("imei", initial?.imei || "");
            form.append("dateRegister", new Date().toISOString());

            const res = await axios.post(
                `${process.env.REACT_APP_API}/fms/fm-data`,
                form,
                { withCredentials: true, headers: { ...getAuthHeaders() } }
            );

            if (res?.data?.msg === "success") {
                onCreated?.();
                resetForm();
                onClose?.();
            } else {
                alert("No se pudo crear el FM. Intenta nuevamente.");
            }
        } catch (e) {
            console.error(e);
            alert("Ocurrió un error guardando el FM.");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fpPopup popupRegister">
            {loading && <PreloadFetch className="mainPreloadOpacity" Loading />}

            <div className="fpPopupCover" onClick={close}></div>

            <div className="fpClosePopupFM" onClick={close}>
                <ImCross />
            </div>

            <div className="fpPopupInt">
                <div className="fpPopupIntCenter">
                    <div className="fpPopupScroll">
                        <div className="parentFormFm fadeInZoom">
                            <h2>Terminar registro FM</h2>

                            <form id="formFm">
                                <div className="wrapperLabelTwo">
                                    <div className="wrapperLabel">
                                        <label htmlFor="source">Fuente</label>
                                        <select id="source" value={segment} disabled>
                                            <option value={1}>Payjoy</option>
                                            <option value={2}>MovilesPay</option>
                                            <option value={3}>Efectiva</option>
                                            <option value={4}>{isCrediSmartCredit ? "CrediSmart" : "MovilPro"}</option>
                                            <option value={5}>Alo Credit</option>
                                        </select>
                                    </div>

                                    {!hideCuota && (
                                        <div className="wrapperLabel formFmComplement">
                                            <label htmlFor="amountSource">Cuota {segmentName}</label>
                                            <span>S/</span>
                                            <input
                                                type="text"
                                                id="amountSource"
                                                value={amountSource}
                                                onInput={amountSourceReadOnly ? undefined : onAmountSourceInput}
                                                readOnly={amountSourceReadOnly}
                                                disabled={amountSourceReadOnly}
                                                placeholder={amountSourceReadOnly ? "Cargada del crédito" : "0.00"}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="wrapperLabel">
                                    <label htmlFor="adressName">Domicilio</label>
                                    <input
                                        type="text"
                                        id="adressName"
                                        value={addressName}
                                        onChange={(e) => setAddressName(e.target.value)}
                                        placeholder="Calle, número, referencia…"
                                    />
                                </div>

                                <div className="wrapperLabel">
                                    <label htmlFor="paymentFrequency">Frecuencia de pago</label>
                                    <select
                                        id="paymentFrequency"
                                        value={pf}
                                        onChange={(e) => setPf(e.target.value)}
                                        disabled={pfDisabled}
                                    >
                                        {pfOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="wrapperLabelTwo">
                                    <div className="wrapperLabel formFmComplement">
                                        <label htmlFor="amountFM">Monto FM</label>
                                        <span>S/</span>
                                        <input type="text" id="amountFM" value={fmt2(amountFM)} readOnly />
                                    </div>

                                    <div className="wrapperLabel">
                                        <label htmlFor="numfee">Cuotas A dividir</label>
                                        <select
                                            id="numfee"
                                            value={numFee}
                                            onChange={(e) => setNumFee(e.target.value)}
                                        >
                                            <option value="" disabled hidden>Selecciona…</option>
                                            {Array.from({ length: 6 }, (_, i) => i + 1).map((i) => {
                                                const pfIndex = Number(pf) > 0 ? Number(pf) - 1 : 0;
                                                const label =
                                                    i === 1
                                                        ? ordinalsTime[pfIndex]?.singular
                                                        : ordinalsTime[pfIndex]?.plural;

                                                return (
                                                    <option key={i} value={i}>
                                                        {i} {label}
                                                    </option>
                                                );
                                            })}

                                        </select>
                                    </div>
                                </div>

                                <div className="wrapperLabelTwo">
                                    <div className="wrapperLabel formFmComplement">
                                        <label htmlFor="fee">Monto Cuota</label>
                                        <span>S/</span>
                                        <input id="fee" value={fee} readOnly disabled />
                                    </div>
                                    <div className="wrapperLabel" />
                                </div>

                                <button type="button" onClick={save}>
                                    Crear FM y guardar registro
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
